import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';

import UserContract from '../../../Model/UserContract';
import useComponentMounted from '../../../hooks/useComponentMounted';
import texts from './texts.json';
import {
  Panel,
  PanelTitle,
  TermsList,
  TermItem,
} from './styles';

const getContractIdFromLink = (link) => {
  if (!link) return '';
  try {
    return new URL(link).searchParams.get('contractId') || '';
  } catch {
    return '';
  }
};

/**
 * Read-only panel that resolves the contract behind a Direct Purchase Link and renders
 * its human-readable terms, so the editor can confirm the email copy matches the contract.
 * Renders nothing unless the link resolves to a contract with terms.
 */
const SelectedPlanTerms = ({ contractLink }) => {
  const [terms, setTerms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const contractId = getContractIdFromLink(contractLink);
    if (!contractId) {
      setTerms([]);
      return;
    }

    const loadTerms = async () => {
      setIsLoading(true);
      try {
        const contract = await UserContract.getContractById(contractId);
        if (isComponentMountedRef.current) {
          setTerms(contract.exists ? contract.terms : []);
        }
      } catch {
        if (isComponentMountedRef.current) {
          setTerms([]);
        }
      } finally {
        if (isComponentMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadTerms();
  }, [contractLink, isComponentMountedRef]);

  // Only show the panel once we have terms to display (or are loading them).
  if (!isLoading && terms.length === 0) {
    return null;
  }

  return (
    <Panel>
      <PanelTitle>{texts.title}</PanelTitle>
      {isLoading ? (
        <CircularProgress size={18} />
      ) : (
        <TermsList>
          {terms.map((term) => (
            <TermItem key={term}>{term}</TermItem>
          ))}
        </TermsList>
      )}
    </Panel>
  );
};

SelectedPlanTerms.propTypes = {
  contractLink: PropTypes.string,
};

SelectedPlanTerms.defaultProps = {
  contractLink: '',
};

export default SelectedPlanTerms;
